import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import {
  ExternalLink,
  ShieldAlert,
  Fingerprint,
  Users,
  Scale,
  ClipboardCheck,
} from 'lucide-react';

// ─── The cyber precedent cycle: now repeating for agents ───────────────
const CYCLE = [
  {
    phase: '1 · SILENCE',
    label: 'The policy neither includes nor excludes the risk',
    marker: 'Silent cyber, pre-2017',
  },
  {
    phase: '2 · DISPUTE',
    label: 'A landmark claim tests the exclusion',
    marker: 'A landmark pharmaceutical cyber-insurance case: $1.4B claim, settled Jan 2024 after 7 years',
  },
  {
    phase: '3 · EXCLUSION',
    label: 'Exclusions written into every renewal',
    marker: "London market bulletin: state-attack exclusion mandated from Mar 2023",
  },
  {
    phase: '4 · PRECONDITION',
    label: 'Controls become preconditions of coverage',
    marker: 'MFA + EDR + tested backups after the ransomware wave',
  },
];

// ─── The five underwriter questions + Kojiru's on-chain answer ──────────
const QUESTIONS = [
  {
    n: 1,
    icon: ClipboardCheck,
    question: 'Do you keep a registry?',
    subq: 'One row per agent: owner, purpose, scope, state.',
    typical:
      'Spreadsheets. Attribution is a week of forensics after the incident, not a lookup.',
    kojiru:
      'Every KYPA-registered agent has an on-chain pledge with a public pledge_id and a live state field readable from any client. Attribution is a single RPC call, not a week.',
    evidence: { label: '/kypa/directory', href: '/kypa/directory' },
  },
  {
    n: 2,
    icon: Scale,
    question: 'Is capability tiered?',
    subq: 'Per-task permissions, not wholesale.',
    typical:
      'Wholesale API keys. If a key is compromised, the blast radius is the entire account.',
    kojiru:
      "The KYPA credential's mandate block encodes max_amount_cents, recipient_allow_list, category, and delivery_target: signed at mint, refused at verify if any field is exceeded.",
    evidence: { label: '/kypa/instrument', href: '/kypa/instrument' },
  },
  {
    n: 3,
    icon: Fingerprint,
    question: 'Does telemetry attribute?',
    subq: 'Identity signed onto every action: liveness alone proves nothing.',
    typical:
      'Log lines with agent name and timestamp. No signature. Non-repudiable? No.',
    kojiru:
      'Every request carries an EIP-712 signature keyed to the ACS signer address. The credential binds identity, mandate, pledge state, and block, in one 65-byte proof.',
    evidence: { label: 'Verify endpoint spec', href: '/kypa/instrument' },
  },
  {
    n: 4,
    icon: ShieldAlert,
    question: 'Is the kill-switch rehearsed?',
    subq: 'A named owner and a tested stop.',
    typical:
      'Documented on a Confluence page. Never fired in anger.',
    kojiru:
      "The arbiter's slash(agentId, debtCents, recipient) function is source-verified on BaseScan. Live test 26 August 2026, tx 0x694f…d276 on Base Sepolia. Pledge 200 → 100, score floored at 300, agent can no longer mint a credential.",
    evidence: {
      label: 'On-chain slash packet',
      href: '/proofs/slash-sepolia-000001.json',
    },
  },
  {
    n: 5,
    icon: Users,
    question: 'Can you produce receipts?',
    subq: 'Audit trails complete enough to hand to a counterparty, or an underwriter.',
    typical:
      "A CSV export, if you're lucky. Not signed. Not chained. Not independently reproducible.",
    kojiru:
      'Every Slashed event on chain triggers a signed JSON packet published at /proofs. Each packet is EIP-191-signed and cryptographically anchored to the block hash. Reproducible by any reader against BaseScan.',
    evidence: { label: '/proofs', href: '/proofs' },
  },
];

export default function AgentInsuranceQuestionnaire() {
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="agent-insurance-questionnaire-page">
      <Helmet>
        <title>The Underwriter's Five Questions: Kojiru's On-Chain Answers</title>
        <meta
          name="description"
          content="The cyber-insurance precedent cycle is repeating for AI agents. The five questions an underwriter will price at next renewal: mapped onto Kojiru's on-chain evidence surface. Every claim publicly verifiable."
        />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <section className="pt-24 pb-14 px-6 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-4">
              THE UNDERWRITER'S FIVE QUESTIONS &middot; MAPPED
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-6 tracking-tight">
              Agent governance is about to be priced.
              <br />
              <span className="text-amber-400">Evidence buys coverage.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-4">
              The cyber-insurance industry has run this cycle once already: silent risk, a
              landmark dispute, exclusions at renewal, then coverage restored only for
              organizations that can evidence controls. It is repeating for AI agents. Below are
              the five questions an underwriter will ask, and the on-chain answer Kojiru
              has already built for each one.
            </p>
            <p className="text-sm text-slate-500 max-w-3xl">
              Every fact on this page is a public court record, a public regulatory bulletin, or
              an on-chain transaction. Every claim is independently verifiable in under a minute
              against a public block explorer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CYCLE STRIP */}
      <section className="py-12 px-6 bg-gradient-to-b from-slate-950 to-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">
            THE CYBER PRECEDENT CYCLE: NOW REPEATING FOR AGENTS
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {CYCLE.map((c) => (
              <div
                key={c.phase}
                className="border border-white/10 rounded-lg p-4 bg-slate-900/60"
                data-testid={`aiq-cycle-${c.phase.split(' ')[0]}`}
              >
                <div className="text-[10px] font-bold tracking-[1.6px] text-amber-400 mb-2">
                  {c.phase}
                </div>
                <div className="text-sm text-white font-semibold leading-snug mb-2">
                  {c.label}
                </div>
                <div className="text-xs text-slate-400 leading-relaxed">{c.marker}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 max-w-3xl leading-relaxed">
            Exclusions move faster than courts. The London insurance market mandated its state-attack exclusion in
            March 2023 while that litigation was still unresolved. Coverage expansion is
            slow; coverage withdrawal is quick, and withdrawal is the first phase.
          </p>
        </div>
      </section>

      {/* THE FIVE QUESTIONS */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-black mb-3 tracking-tight">
            The five questions, and where Kojiru answers each one.
          </h2>
          <p className="text-sm text-slate-400 mb-10 max-w-3xl leading-relaxed">
            Each question is the breach post-mortem written in a different hand. Every
            &ldquo;Kojiru answer&rdquo; links to a live surface a reader can inspect right now.
          </p>

          <div className="space-y-4">
            {QUESTIONS.map((q) => {
              const Icon = q.icon;
              return (
                <div
                  key={q.n}
                  className="border border-white/10 rounded-xl overflow-hidden bg-slate-900/50"
                  data-testid={`aiq-question-${q.n}`}
                >
                  <div className="p-6 border-b border-white/5 flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-[1.6px] text-amber-400 mb-1">
                        QUESTION {q.n}
                      </div>
                      <div className="text-lg font-bold text-white leading-tight">
                        {q.question}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">{q.subq}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/5">
                    <div className="p-5">
                      <div className="text-[10px] font-bold tracking-[1.6px] text-slate-500 mb-2">
                        TYPICAL ENTERPRISE ANSWER
                      </div>
                      <div className="text-sm text-slate-300 leading-relaxed">{q.typical}</div>
                    </div>
                    <div className="p-5 bg-emerald-950/20">
                      <div className="text-[10px] font-bold tracking-[1.6px] text-emerald-400 mb-2">
                        KOJIRU'S ON-CHAIN ANSWER
                      </div>
                      <div className="text-sm text-slate-200 leading-relaxed mb-3">
                        {q.kojiru}
                      </div>
                      <Link
                        to={q.evidence.href}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-md px-2.5 py-1.5"
                        data-testid={`aiq-evidence-link-q${q.n}`}
                      >
                        {q.evidence.label} <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NOT INSURANCE, NOT A PROMISE */}
      <section className="py-14 px-6 bg-slate-900/50 border-y border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">POSITIONING</div>
          <h2 className="text-2xl font-black text-white mb-4 leading-tight">
            Not insurance. Not a promise to pay. Collateral, on-chain, deterministic.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            Kojiru is not an insurance product and does not underwrite risk. The KYPA pledge is
            economically real collateral posted by the agent's operator to a source-verified
            contract on Base, and the slash rule is expressed in that contract's public source,
            not in a policy wording. When harm is affirmed, the collateral moves to the harmed
            party in a single on-chain transaction, not as a claim, not as a promise, not
            subject to operator discretion.
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            The practical consequence: an enterprise deploying agents against a KYPA pool moves
            its uncapped downstream harm exposure onto a pre-priced, publicly verifiable,
            deterministic settlement instrument. That instrument is the artifact an underwriter's
            questionnaire will price against.
          </p>
          <p className="text-slate-300 leading-relaxed">
            Model and platform providers cap liability at fees paid. When an agent crosses an
            organizational boundary and causes harm, the counterparty sues the deployer, not the
            model provider. Kojiru is where the deployer moves that exposure.
          </p>
        </div>
      </section>

      {/* WHAT THIS PAGE DOES NOT CLAIM */}
      <section className="py-14 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-red-400 mb-3">
            WHAT THIS PAGE DOES NOT CLAIM
          </div>
          <h2 className="text-2xl font-black text-white mb-6 leading-tight">
            The honest register: same treatment as our /readiness page.
          </h2>
          <ul className="space-y-3 text-sm text-slate-300 leading-relaxed">
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                Kojiru is not an insurance carrier. It does not underwrite risk, does not issue
                policies, and does not accept premiums. The pledge-and-slash instrument is a
                cryptographic collateral primitive, not a regulated insurance contract.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                The first live slash executed on Base Sepolia, a public test network, using a
                Kojiru-branded mock ERC-20 (mKOJI) with no production value. It is a mechanism
                demonstration, not a consumer-scale mainnet event. The USDC-bound mainnet Hook is
                in advisor-led deployment as of August 2026.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                The arbiter is a Kojiru-controlled key today. Transition to a multi-signature
                quorum, and subsequently to a rules-based arbiter with dispute-evidence linkage,
                is published roadmap, not shipped state.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-red-400 flex-shrink-0">&rarr;</span>
              <div>
                No commercial underwriter has priced against a Kojiru pledge yet. This page argues
                that the pool <em>is</em> the artifact underwriters will price against, 
                that argument has not yet been settled by the market.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* SOURCES */}
      <section className="py-12 px-6 bg-slate-900/40 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-4">
            SOURCES &middot; ALL PUBLIC
          </div>
          <ul className="space-y-2 text-xs text-slate-400 leading-relaxed">
            <li>
              <strong className="text-slate-200">A landmark pharmaceutical cyber-insurance case</strong>{' '}
              New Jersey Superior Court and Appellate Division. $1.4B claim under a
              property policy over NotPetya, settled January 2024. Publicly reported and
              recorded in the state court system.
            </li>
            <li>
              <strong className="text-slate-200">A London insurance market bulletin</strong>, August
              2022: mandating explicit state-attack exclusions on all standalone cyber
              policies from March 2023. Published on lloyds.com.
            </li>
            <li>
              <strong className="text-slate-200">Kojiru on-chain slash</strong> Base
              Sepolia, chain ID 84532, KojiruACSHook{' '}
              <code className="text-slate-300">0x5662668FB18bc504e05f4809005f8caa104103BA</code>.
              Slash tx{' '}
              <code className="text-slate-300 break-all">
                0x694ffe5f5a35dbf8e1267110987f802ec6b4173772c4627f7495ca2e2ef1d276
              </code>
              , block 43445800, 26 August 2026.
            </li>
            <li>
              <strong className="text-slate-200">
                KYPA Credential Envelope v1.0 specification
              </strong>{' '}
              {' '}
              <Link to="/kypa/instrument" className="text-cyan-300 hover:underline">
                kojiru.com/kypa/instrument
              </Link>
              .
            </li>
            <li>
              <strong className="text-slate-200">Enterprise readiness self-audit</strong> {' '}
              <Link to="/readiness" className="text-cyan-300 hover:underline">
                kojiru.com/readiness
              </Link>
              .
            </li>
          </ul>
          <p className="text-xs text-slate-500 mt-6 italic">
            The only third-party names on this page are parties to public court cases and
            regulatory bulletins: institutional public-record actors, not private
            commentators: consistent with Kojiru's standing third-party naming policy.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
